import { type ElementType, forwardRef, type RefObject } from 'react';

import { Icon } from '~/components/granular';
import { classNames } from '~/utils/styles';

import type { LinkProps } from './Link.types';

type RefType = RefObject<HTMLButtonElement> | RefObject<HTMLAnchorElement>;

export const Link = forwardRef<RefType, LinkProps>(
  (
    { as = 'a', appearance = 'default', leftIcon, rightIcon, className, children, size = 'md', ...props },
    forwardedRef,
  ) => {
    const classes = classNames(
      'cursor-pointer items-center disabled:cursor-not-allowed disabled:text-neutral-600',
      {
        'no-underline!': appearance === 'none',
        'button-200 flex': as === 'button' && !['none', 'plain'].includes(appearance),
        'inline-flex': appearance !== 'none',
        'label-200': size === 'sm',
        'link-100': size === 'lg',
        'link-200': size === 'md',
        'text-neutral-1000 hover:text-neutral-1000 focus:bg-teal-200 active:text-teal-900': appearance === 'secondary',
        'text-red-700 hover:text-red-800 focus:bg-red-200 active:text-red-900': appearance === 'danger',
        'text-teal-700 hover:text-teal-800 focus:bg-teal-200 active:text-teal-900': appearance === 'default',
        'text-white hover:text-teal-200 focus:text-teal-200 active:text-teal-200': appearance === 'white',
        'underline hover:no-underline': appearance === 'plain',
      },
      className,
    );

    const Element = as as ElementType;

    return (
      <Element className={classes} ref={forwardedRef} role={as === 'a' ? 'link' : 'button'} {...props}>
        {leftIcon && <Icon className="inline-block pr-1" icon={leftIcon} />}
        {children}
        {rightIcon && <Icon className="inline-block pl-1" icon={rightIcon} />}
      </Element>
    );
  },
);
